'use client';

import { FormattedMessage } from 'react-intl';
import CardLocation from '@alltrails/amplitude/enums/CardLocation';
import logTrailCardClicked from '@alltrails/amplitude/events/logTrailCardClicked';
import logTrailDetailsNearbyTrailClicked from '@alltrails/amplitude/events/logTrailDetailsNearbyTrailClicked';
import TrailStandardCard from '@alltrails/cards/components/TrailStandardCard';
import { PageStrings } from '@alltrails/core/constants/pageStringHelpers';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import useMediaQuery from '@alltrails/core/hooks/useMediaQuery';
import Carousel from '@alltrails/denali/components/Carousel';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { BREAKPOINT_MD_SIZE } from '@alltrails/denali/tokens';
import useReduxListItems from '@alltrails/lists/hooks/useReduxListItems';
import type { TrailPage } from '@alltrails/trails';
import { trailFromAlgoliaTrail } from '@alltrails/trails';
import type Trail from '@alltrails/types/Trail';
import { useAppSelector } from '../../utils/redux';
import SectionHeader from '../SectionHeader';
import styles from './NearbyTrails.module.scss';

const reduxListsAnalyticsInfo = { detailedCardLocation: 'nearby trail card' as CardLocation, page: PageStrings.TRAIL_PAGE };

type NearbyTrailsProps = {
  mainTrailId: number;
  nearbyTrails: TrailPage['trailPageProps']['nearbyTrails'];
  isBot: boolean;
};

const NearbyTrails = ({ mainTrailId, nearbyTrails, isBot }: NearbyTrailsProps) => {
  const languageRegionCode = useLanguageRegionCode();
  const isMediumDown = useMediaQuery(`(max-width: ${BREAKPOINT_MD_SIZE}px)`);
  const displayMetric = useAppSelector(state => state.context.displayMetric);
  const { isFavorite, isComplete, isVerified, handleFavoriteClick, listModal, signUpModal } = useReduxListItems(
    undefined,
    false /* preventAutoInitialize */,
    reduxListsAnalyticsInfo
  );
  const isMobile = useIsMobileSizedScreen();

  const onTrailClick = (trail: Trail) => {
    logTrailDetailsNearbyTrailClicked({ trail_id: mainTrailId, destination_trail_id: trail.id });
    logTrailCardClicked({
      trail_id: String(trail.id),
      card_location: 'web trail page',
      detailed_card_location: 'web trail page top module'
    });
  };

  const trailsList = isBot ? nearbyTrails : nearbyTrails.slice(0, 12);

  return (
    <section>
      <SectionHeader title={<FormattedMessage defaultMessage="Top trails nearby" />} />
      {isBot ? (
        <div className={styles.nearbyTrailsBotContainer}>
          {trailsList.map(trail => {
            const renderableTrail = trailFromAlgoliaTrail(trail);
            return (
              <TrailStandardCard
                key={trail.id}
                trail={renderableTrail}
                languageRegionCode={languageRegionCode}
                useMetric={displayMetric}
                isPhotoZoomEnabled
                maxPhotos={1}
                isCompleted={isComplete('trail', renderableTrail.id)}
                isFavorite={isFavorite('trail', renderableTrail.id)}
                isVerified={isVerified('trail', renderableTrail.id)}
                onFavoriteClick={() => handleFavoriteClick('trail', renderableTrail.id)}
                onCardClick={() => onTrailClick(renderableTrail)}
              />
            );
          })}
        </div>
      ) : (
        <Carousel
          uniqueId="nearby-trails-carousel"
          getItemKey={trail => `${trail.ID}`}
          items={trailsList}
          renderItem={item => {
            const trail = trailFromAlgoliaTrail(item);
            return (
              <TrailStandardCard
                key={trail.id}
                trail={trail}
                languageRegionCode={languageRegionCode}
                useMetric={displayMetric}
                isPhotoZoomEnabled
                maxPhotos={1}
                isCompleted={isComplete('trail', trail.id)}
                isFavorite={isFavorite('trail', trail.id)}
                isVerified={isVerified('trail', trail.id)}
                onFavoriteClick={() => handleFavoriteClick('trail', trail.id)}
                onCardClick={() => onTrailClick(trail)}
              />
            );
          }}
          showOverflow={isMediumDown}
          slidesPerViewMobile={1}
          slidesPerViewBreakpoints={{ 541: 2, 1121: 3 }}
          showPaginationDots={false}
          className={isMobile ? styles.mobileCarousel : ''}
        />
      )}
      {listModal}
      {signUpModal}
    </section>
  );
};

export default NearbyTrails;
